import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Form, Input, Modal, Table, Tag, Tooltip, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { useBusinessDay } from '../../../businessDay/BusinessDayContext.js';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { applyFeesBox, feeOf } from '../../../domain/feeRate.js';
import { formatInstant, formatMoney, formatUnits } from '../../../format';
import { AwaitingRepricing, BasisTag, FeeCell, PriceCell, RequestTypeCell, ReviewsCell, StateTag } from './orderColumns.jsx';
import { cutoffBanner, figureOf, reviewEntry } from './orderRows.js';
import PayoutAccounts from './PayoutAccounts.jsx';
import {
    COMMENT_MAX, ORDER_TYPE_COLOUR, ORDER_TYPE_LABEL, commentProblem, feesWaivedChange, otherLegLabel, reviewRequest
} from './orders.js';

const { Text } = Typography;

const dash = () => <Text type="secondary">—</Text>;

/** A Units or Amount figure, read-only, with its price basis (orderRows.js figureOf). */
const Figure = ({ order, side, format }) => {
    const { value, tag, awaitingRepricing } = figureOf(order, side);
    if (awaitingRepricing) return <AwaitingRepricing />;
    if (value === null || value === undefined) return dash();
    return <span className="order-figure-cell">{format(value)}<BasisTag tag={tag} /></span>;
};

/**
 * IP-203: one leg's Apply fees checkbox - ticked by default where the fund
 * charges this leg's kind of fee; off and disabled, with the reason on hover,
 * where it charges none or the leg is a transfer (domain/feeRate.js
 * applyFeesBox puts the server's decision into words). The tooltip sits on a
 * wrapper so it shows on a disabled box too, and reaches a keyboard.
 *
 * IP-204: "by default" is the fund's rule - a fund may tick the box only when
 * the transaction's amount is more than an amount it sets - so the box starts
 * as the server sends it (fees.applied), with the rule's comparison in the
 * tooltip. Saving the review keeps a box left as shown.
 *
 * @param {{ order: object, checked: Record<string, boolean>, onChange: (orderId: string, value: boolean) => void }} props
 */
export const ApplyFeesBox = ({ order, checked, onChange }) => {
    const box = applyFeesBox(order);
    const value = box.enabled ? (checked?.[order.orderId] ?? box.checked) : false;
    return (
        <Tooltip title={box.tooltip}>
            <span className="review-dialog-apply-fees" tabIndex={box.enabled ? -1 : 0}>
                <Checkbox
                    checked={value}
                    disabled={!box.enabled}
                    aria-label={'Apply fees on ' + (order.transactionRef ?? order.orderId)}
                    onChange={(e) => onChange(order.orderId, e.target.checked)}
                >
                    Apply fees
                </Checkbox>
            </span>
        </Tooltip>
    );
};

/** The dealing date, with the provisional tag a subscription's carries until its money clears. */
const DealingDate = ({ order }) => (order.dealingDate ? (
    <span className="dealing-date-cell">
        {order.dealingDate}
        {order.dealingDateBasis === 'PROVISIONAL' && <Tag className="dealing-date-prov">prov.</Tag>}
    </span>
) : dash());

/**
 * One order's figures as the reviewer reads them: read-only, each provisional
 * figure with its basis (design 8.3). A pair's other leg is named for what it
 * is to the holder (orders.js otherLegLabel), and its ID opens it here
 * (onShow), so the reviewer can read both legs' figures before accepting them
 * together.
 */
const OrderDetails = ({ order, partners, checked, onFeesChange, onShow = null }) => (
    <>
        <div className="review-dialog-id-row">
            <span className="review-dialog-label">Transaction ID</span>
            <span className="txn-id">{order.transactionRef}</span>
        </div>
        <div className="review-dialog-meta">
            <div className="review-dialog-detail"><span className="review-dialog-label">Type</span><Tag color={ORDER_TYPE_COLOUR[order.orderType]} className="review-dialog-status-tag">{ORDER_TYPE_LABEL[order.orderType] ?? order.orderType}{order.isFullHolding ? ' (full holding)' : ''}</Tag></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">State</span><StateTag order={order} /></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Request type</span><RequestTypeCell row={order} /></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Fund</span><strong>{order.fundId}</strong></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Holder</span><strong>{order.holderNo ?? '—'}</strong></div>
            {/* IP-202: the strategy activation code is shown at review. */}
            <div className="review-dialog-detail"><span className="review-dialog-label">Strategy code</span><span className="txn-id">{order.strategyActivationCode ?? '—'}</span></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Amount (LKR)</span><strong><Figure order={order} side="amount" format={formatMoney} /></strong></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Units</span><strong><Figure order={order} side="units" format={formatUnits} /></strong></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Unit price</span><PriceCell order={order} /></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Effective date</span><DealingDate order={order} /></div>
            {/* The fee and its % take the whole row, so a long % wraps under the figure instead of running into the next column. */}
            {(feeOf(order) || order.feeAmount) && <div className="review-dialog-detail review-dialog-wide"><span className="review-dialog-label">Fee (LKR)</span><FeeCell order={order} /></div>}
            <div className="review-dialog-detail"><span className="review-dialog-label">Fees</span><ApplyFeesBox order={order} checked={checked} onChange={onFeesChange} /></div>
            <div className="review-dialog-detail"><span className="review-dialog-label">Reviews</span><ReviewsCell order={order} /></div>
        </div>
        {partners.map((leg) => (
            <div key={leg.orderId} className="review-dialog-partner">
                <div className="review-dialog-partner-row">
                    <span className="review-dialog-label">{otherLegLabel(leg.orderType)}</span>
                    <div className="review-dialog-partner-values">
                        {onShow
                            ? (
                                <Button type="link" size="small" className="txn-id txn-id-link" onClick={() => onShow(leg.orderId)}
                                    aria-label={'Show ' + (leg.transactionRef ?? leg.orderId) + ' in this review'}>
                                    {leg.transactionRef}
                                </Button>
                            )
                            : <span className="txn-id">{leg.transactionRef}</span>}
                        {/* A transfer's other leg is another strategy: its code too (IP-202). */}
                        <span className="txn-id">{leg.strategyActivationCode ?? '—'}</span>
                        <StateTag order={leg} />
                        {/* IP-203: each leg its own choice - a switch-in may pay the target fund's front-end fee. */}
                        <ApplyFeesBox order={leg} checked={checked} onChange={onFeesChange} />
                    </div>
                </div>
                <Text type="secondary">Both legs of a switch or transfer are reviewed together.</Text>
            </div>
        ))}
        {order.repricedSinceReview && (
            <div className="review-dialog-hint">Priced again since the last review: the figures above are the new ones.</div>
        )}
    </>
);

/** Several orders in a compact read-only table (design 8.3), with the refined table's look. */
const OrdersTable = ({ orders, checked, onFeesChange }) => (
    <Table
        className="pending-transactions-table review-dialog-table"
        rowKey="orderId"
        size="small"
        pagination={false}
        dataSource={orders}
        scroll={{ x: 'max-content', y: 280 }}
        columns={[
            { title: 'Transaction ID', dataIndex: 'transactionRef', key: 'transactionRef', render: (ref) => <span className="txn-id">{ref}</span> },
            { title: 'Type', dataIndex: 'orderType', key: 'orderType', render: (type) => <Tag color={ORDER_TYPE_COLOUR[type]}>{ORDER_TYPE_LABEL[type] ?? type}</Tag> },
            { title: 'Request type', key: 'requestType', render: (_, order) => <RequestTypeCell row={order} /> },
            { title: 'Fund', dataIndex: 'fundId', key: 'fundId' },
            // IP-202: the strategy activation code is shown at review.
            { title: 'Strategy code', dataIndex: 'strategyActivationCode', key: 'strategyActivationCode', render: (v) => <span className="txn-id">{v ?? '—'}</span> },
            { title: 'Amount (LKR)', key: 'amount', render: (_, order) => <Figure order={order} side="amount" format={formatMoney} /> },
            { title: 'Units', key: 'units', render: (_, order) => <Figure order={order} side="units" format={formatUnits} /> },
            { title: 'Unit price', key: 'unitPrice', render: (_, order) => <PriceCell order={order} /> },
            { title: 'Effective date', key: 'dealingDate', render: (_, order) => <DealingDate order={order} /> },
            { title: 'State', key: 'state', render: (_, order) => <StateTag order={order} /> },
            { title: 'Reviews', key: 'reviews', render: (_, order) => <ReviewsCell order={order} /> },
            // IP-203: each transaction's Apply fees, set here for this review.
            { title: 'Fees', key: 'applyFees', render: (_, order) => <ApplyFeesBox order={order} checked={checked} onChange={onFeesChange} /> }
        ]}
    />
);

/**
 * The order's earlier reviews and send-backs, oldest first - who, when, and
 * what they wrote (design 8.3). Every reviewer reads them; a read that failed
 * says why and can be tried again (onRetry).
 */
export const PreviousReviews = ({ reviews, loading = false, error = null, onRetry = null }) => {
    if (error) {
        return (
            <Text type="secondary">
                The earlier reviews could not be read: {error}
                {onRetry && <> <Button type="link" size="small" onClick={onRetry}>Try again</Button></>}
            </Text>
        );
    }
    if (loading) return <Text type="secondary">Reading the earlier reviews…</Text>;
    if (!reviews?.length) return <Text type="secondary">Nobody has reviewed this transaction yet.</Text>;
    return (
        <ul className="review-history-list">
            {reviews.map((r) => {
                const entry = reviewEntry(r);
                return (
                    <li key={r.reviewId}>
                        <div className="review-history-by">
                            <Text strong>{entry.who}</Text>
                            <Tag color={entry.sentBack ? 'orange' : 'green'}>{entry.kind}</Tag>
                            <Text type="secondary">{formatInstant(r.occurredAt)}</Text>
                        </div>
                        {entry.text && <div className="review-history-text">{entry.text}</div>}
                    </li>
                );
            })}
        </ul>
    );
};

/** The comment rule as the form's validator: the catalogue's own (orders.js commentProblem). */
const commentRule = { validator: (_, value) => (commentProblem(value) ? Promise.reject(new Error(commentProblem(value))) : Promise.resolve()) };

/**
 * The dialog's body (design 8.3), apart from the Modal so a render test can
 * read it: the order (a details grid for one, a compact table for several),
 * its provisional figures read-only with their basis, for one order its
 * earlier reviews, the cut-off banner, and the mandatory comment.
 *
 * @param {{ orders: object[], primary: object|null, reviews?: object[]|null, reviewsLoading?: boolean,
 *   reviewsError?: string|null, error?: string|null, nowMs?: number, form?: object, onFinish?: (values) => void,
 *   checked?: Record<string, boolean>, onFeesChange?: (orderId: string, value: boolean) => void }} props
 *   checked: IP-203's Apply fees boxes as the reviewer has left them (orderId -> ticked); a leg not in it shows its own
 */
export const ReviewDialogContent = ({
    orders, primary, reviews = null, reviewsLoading = false, reviewsError = null, payoutAccounts = null, error = null, nowMs = Date.now(), form, onFinish,
    checked = {}, onFeesChange = () => {}, shownId = null, onShow = null, onRetryReviews = null
}) => {
    const banner = cutoffBanner(orders, nowMs);
    // The leg shown in full: the one the reviewer opened, or the other leg once its ID is clicked (shownId).
    const shown = primary ? (orders.find((o) => o.orderId === shownId) ?? primary) : null;
    const partners = shown ? orders.filter((o) => o.orderId !== shown.orderId) : [];
    return (
        <>
            {banner && (
                <div className="permission-info-banner review-dialog-info">
                    <ActionIcon name="info" />
                    <span>{banner}</span>
                </div>
            )}
            {shown
                ? <OrderDetails order={shown} partners={partners} checked={checked} onFeesChange={onFeesChange} onShow={onShow} />
                : <OrdersTable orders={orders} checked={checked} onFeesChange={onFeesChange} />}
            {/* A redemption paid to several bank accounts: what each account is paid, so the reviewer checks it. */}
            {shown && payoutAccounts?.length > 0 && (
                <>
                    <h4 className="review-dialog-section">Payout accounts</h4>
                    <PayoutAccounts detail={{ order: shown, payoutAccounts }} />
                </>
            )}
            {shown && (
                <>
                    <h4 className="review-dialog-section">Previous reviews</h4>
                    <PreviousReviews reviews={reviews} loading={reviewsLoading} error={reviewsError} onRetry={onRetryReviews} />
                </>
            )}
            {error && <Alert type="error" showIcon title={error} className="auth-alert review-dialog-error" />}
            <Form form={form} layout="vertical" onFinish={onFinish} className="review-dialog-form">
                <Form.Item
                    name="comment"
                    label={orders.length > 1 ? 'Comment (for every transaction here)' : 'Comment'}
                    rules={[{ required: true, message: commentProblem('') }, commentRule]}
                    validateFirst
                    extra="What you checked. The comment is kept with the review and shown to everyone who opens the transaction."
                >
                    <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={COMMENT_MAX} showCount placeholder="For example: amount and holder checked against the instruction" />
                </Form.Item>
            </Form>
        </>
    );
};

/**
 * Review one order or many (IP-127; design 8.3), replacing the review modals
 * that set a fee (IP-136: the fee is the fund's rate, never typed). The
 * review is mandatory and its comment too, and several people may review the
 * same order: the comment says what this reviewer checked, and it is kept
 * with the review and a snapshot of the figures they saw.
 *
 * Every leg also shows its Apply fees checkbox (IP-203): ticked by default
 * where the fund charges the leg's kind of fee, off and disabled - with why -
 * where it charges none or the leg is a transfer. Fee percentages are the
 * fund's and are never typed here; a changed box is sent as feesWaived, and
 * the server prices the transaction with or without the fee.
 *
 * It submits api.orders.review(orderIds, expectedVersions, comment): the
 * versions are the ones the screen showed, so an order that moved since is
 * skipped with the server's "refresh and review again" (APR-012). A review
 * the business day pauses (REVIEW_PAUSED, 409) is refused whole, in the
 * server's words, and the business day is read again so the page greys the
 * buttons it covers. What the server skipped is said by the caller's
 * summary, in its own words.
 *
 * @param {{ orders: object[], primary: object|null, open: boolean, onClose: () => void, onDone: (result: object) => void }} props
 *   orders: every order of the review, a pair's other leg included; primary: the order the person opened it on (one
 *   order and its partner), or null for a selection
 */
const ReviewDialog = ({ orders, primary, open, onClose, onDone }) => {
    const { api } = useAccess();
    const { refresh } = useBusinessDay();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [reviews, setReviews] = useState({ list: null, loading: false, error: null });
    // The primary order's payout accounts, read with its reviews from the same detail.
    const [payoutAccounts, setPayoutAccounts] = useState(null);
    // IP-203: the Apply fees boxes the reviewer changed in this opening (orderId -> ticked).
    const [checked, setChecked] = useState({});
    const primaryId = primary?.orderId ?? null;
    // The leg shown in full: the one opened, until the reviewer clicks the other leg's ID.
    const [shownId, setShownId] = useState(primaryId);
    // Bumped by "Try again" to read the earlier reviews once more.
    const [attempt, setAttempt] = useState(0);

    // destroyOnHidden gives every opening a fresh form; the last refusal and the earlier reviews are read again.
    useEffect(() => {
        if (!open) return;
        setError(null);
        setChecked({});
        setShownId(primaryId);
    }, [open, primaryId]);

    // The shown leg's earlier reviews and payout accounts, from its detail. A request that never reached the
    // server (no status: the network, or a connection dropped on the way) is tried once more by itself before
    // the reviewer is told - and they can try again from the message.
    const detailId = shownId ?? primaryId;
    useEffect(() => {
        if (!open) return undefined;
        if (!detailId) {
            setReviews({ list: null, loading: false, error: null });
            setPayoutAccounts(null);
            return undefined;
        }
        let live = true;
        setReviews({ list: null, loading: true, error: null });
        const read = (retry) => api.orders.detail(detailId)
            .then((detail) => {
                if (!live) return;
                setReviews({ list: detail.reviews ?? [], loading: false, error: null });
                setPayoutAccounts(detail.payoutAccounts ?? []);
            })
            .catch((e) => {
                if (!live) return;
                if (retry && !e.status) {
                    setTimeout(() => live && read(false), 1500);
                    return;
                }
                setReviews({ list: null, loading: false, error: e.message });
            });
        read(true);
        return () => {
            live = false;
        };
    }, [api, open, detailId, attempt]);

    const submit = async ({ comment }) => {
        // IP-203: only the boxes the reviewer changed are sent.
        const { body, message } = reviewRequest(orders, comment, { feesWaived: feesWaivedChange(orders, checked) });
        if (!body) {
            setError(message);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            onDone(await api.orders.review(body.orderIds, body.expectedVersions, body.comment, body.feesWaived ?? null));
        } catch (e) {
            setError(e.message);
            // The business day moved under the page (IP-129): read it again, so the buttons it pauses grey.
            if (e.code === 'REVIEW_PAUSED') refresh();
        } finally {
            setBusy(false);
        }
    };

    const title = primary
        ? 'Review - ' + primary.transactionRef
        : 'Review ' + orders.length + ' transaction' + (orders.length === 1 ? '' : 's');

    return (
        <Modal
            open={open && orders.length > 0}
            title={title}
            okText="Accept Transaction"
            cancelText="Cancel"
            okButtonProps={{ loading: busy }}
            cancelButtonProps={{ disabled: busy }}
            onOk={() => form.submit()}
            onCancel={onClose}
            width={primary ? 640 : 980}
            centered
            maskClosable={false}
            rootClassName="lfoc-styles-wrapper review-dialog"
            destroyOnHidden
        >
            <ReviewDialogContent
                orders={orders}
                primary={primary}
                reviews={reviews.list}
                reviewsLoading={reviews.loading}
                reviewsError={reviews.error}
                payoutAccounts={payoutAccounts}
                error={error}
                form={form}
                onFinish={submit}
                checked={checked}
                onFeesChange={(orderId, value) => setChecked((was) => ({ ...was, [orderId]: value }))}
                shownId={shownId}
                onShow={setShownId}
                onRetryReviews={() => setAttempt((n) => n + 1)}
            />
        </Modal>
    );
};

export default ReviewDialog;
